/**
 * The `{ error }` contract, as CODES rather than sentences.
 *
 * A refusal from the Worker is rendered verbatim in a dialog: `FormError`
 * shows `ApiError.message` because the server's reason ("3 games still
 * reference this team") is the only useful thing to say. Once the UI speaks two
 * languages that sentence cannot be written on the server: the Worker has no
 * business knowing which language the phone that called it is set to, and
 * baking one in would have picked a language at deploy time for every future
 * tournament.
 *
 * So the wire carries a code and its parameters, and the client renders the
 * sentence out of its own dictionary. `src/lib/api.ts` translates on the way
 * into `ApiError`, which is why not one of the thirteen call sites that display
 * `ApiError.message` had to change.
 *
 * Codes are a CONTRACT: renaming one is a breaking change to a deployed client
 * exactly like renaming a route. Adding one means adding it here, in the two
 * dictionaries, and in `test/i18n.test.ts` (which proves every code has a
 * sentence in both).
 */

/** A sentence that interpolates nothing. NOT `Record<string, never>`: that has
 *  `keyof` of `string`, which would make every code look parameterised. */
type NoParams = Record<never, never>;

/**
 * Every code, with the parameters its sentence interpolates. `NoParams` means
 * the sentence takes none.
 *
 * `session-*` codes are the odd ones out: `src/lib/session.ts` has always read
 * `{ error }` off the token exchange as a machine value ("revoked" is a
 * different screen from "wrong key"), so those keep the exact strings they had.
 */
export interface ApiErrorParams {
  "fan-closed": NoParams;
  "fan-choice": NoParams;
  "pickup-already-rostered": NoParams;
  "roster-full": NoParams;
  "already-member": NoParams;
  "application-pending": NoParams;
  "application-missing": NoParams;
  "player-claimed": NoParams;
  /** The player declined this squad's pickup invitation at this event; the
   *  squad does not ask again, the player may still ask it (2026-09-15). */
  "pickup-declined": NoParams;
  /** A squad's team-issued pickup invitations and links for the rolling day
   *  are used up (`PICKUP_TEAM_INVITES_PER_DAY`). */
  "pickup-invite-limit": NoParams;
  /** A pickup invite link that does not verify. Not `invalid`, whose sentence
   *  is about signing in. */
  "pickup-link-invalid": NoParams;
  /** Somebody already joined through this single-use link. */
  "pickup-link-used": NoParams;
  /** A message posted to a support chat one side already ended (2026-09-16). */
  "support-closed": NoParams;
  // ---- generic ----
  /** A malformed body. `detail` says which parser refused, for a human with
   *  curl; the sentence shown is the same either way, because a client that
   *  sends a bad payload has a bug rather than a user problem. */
  "invalid-payload": NoParams;
  "not-found": NoParams;
  "server-error": NoParams;
  forbidden: NoParams;
  unauthorized: NoParams;
  "expected-websocket": NoParams;
  /** Password re-prompt needed for a destructive platform action. */
  "reauth-required": NoParams;

  // ---- session exchange (read as machine values by lib/session.ts) ----
  invalid: NoParams;
  revoked: NoParams;
  expired: NoParams;
  rate: NoParams;
  /**
   * The link verified, and opening it here would REPLACE a credential this
   * browser already holds in the same slot. Refused rather than performed:
   * a grant token is shown exactly once, so silently swapping one for another
   * can leave the organiser with no way back into /admin. The body carries
   * `incoming` and `replacing` so the interstitial can name both.
   */
  "would-replace": NoParams;

  // ---- accounts and invitations (V2 Phase 3) ----
  /** The invite has already been accepted. Only ever returned once the secret
   *  has verified, like `revoked` and `expired`. */
  "invite-accepted": NoParams;
  /** A membership invite opened with nobody signed in and no registration
   *  form filled: the client shows the form. Never shown to a person. */
  "team-leader-ineligible": NoParams;
  "invite-account-mismatch": NoParams;
  "need-registration": NoParams;
  /** The account this sign-in or invite names has been disabled. */
  "account-disabled": NoParams;
  /** A reset invite for an account that no longer exists. */
  "account-missing": NoParams;

  // ---- tournament data ----
  "team-referenced": { games: number };
  /** Import: this tournament already has the team (the club's id). */
  "team-exists": NoParams;
  /** Promote: a club with this team's id already exists. */
  "club-exists": NoParams;
  /** An organiser inviting onto a club that already has people (D15). */
  "club-owned": NoParams;
  /** Sync: this team was never imported from or promoted into the library. */
  "team-not-in-library": NoParams;
  "club-missing": NoParams;
  "field-has-games": { games: number };
  "field-last": NoParams;
  "too-many-games": NoParams;
  "unknown-team": NoParams;
  "person-already-merged": { clash: string };

  // ---- games and spirit ----
  "game-off-schedule": NoParams;
  /** A correction with no points aimed at a snapshot that has them, without
   *  the editor's explicit erase acknowledgement (`allowErase`). The record
   *  stays; the client re-sends with the flag after the organiser confirms. */
  "erase-refused": NoParams;
  "game-not-scheduled": NoParams;
  "game-not-played": NoParams;
  "opponent-undecided": NoParams;
  "team-not-in-game": NoParams;
  "spirit-off": NoParams;
  /** The team has already filed this score. It is not an edit conflict and not
   *  a permission problem: a filed opinion is changed by the organiser, who
   *  leaves a trail on the record when they do. */
  "spirit-filed": NoParams;

  "self-scoring-off": NoParams;

  // ---- push ----
  /** No VAPID keypair on this deployment, so push is not wired up here. A
   *  configuration fact, not a user error, and
   *  the control on `/team` is simply never offered. */
  "push-off": NoParams;

  // ---- result cards ----
  /** No card-renderer configured (`CARD_RENDER_URL`/`CARD_RENDER_KEY`). Same
   *  shape as `push-off`: rendering runs on the owner's VPS
   *  because the Worker's Free-plan CPU budget cannot carry satori+resvg, and
   *  a deployment without one is a valid deployment whose cards are off. */
  "cards-off": NoParams;

  // ---- platform accounts ----
  "superadmin-exists": NoParams;
  "email-registered": NoParams;
  "superadmin-last": NoParams;
  "superadmin-self": NoParams;
  "superadmin-missing": NoParams;

  // ---- tournaments (the registry, V2 Phase 0) ----
  /** Another tournament already has this slug. */
  "slug-taken": NoParams;
  /** The slug is a word the app keeps for itself (`RESERVED_SLUGS`). */
  "slug-reserved": NoParams;
  /** The slug cannot change once the tournament is published: it is printed
   *  on QR codes and typed into OBS by then (D4). */
  "slug-frozen": NoParams;
  /** The tournament a write names does not exist in the registry. */
  "tournament-missing": NoParams;
  /** An archived tournament refuses writes (D16). Reserved here so the code
   *  exists; nothing raises it until Phase 4 wires `TournamentDO.isWritable`
   *  into the mutating RPCs. */
  "tournament-archived": NoParams;
  /** The existing event is already registered; adoption happens once. */
  "already-adopted": NoParams;
  /** A club entering or withdrawing while the tournament is not taking
   *  registration (2026-09-11). */
  "registration-closed": NoParams;
  "roster-locked": NoParams;

  // ---- notifications and Telegram (V3 Phases 0-1) ----
  /** The dev outbox (`GET /api/super/outbox`) exists only in LOG MODE; with
   *  `NOTIFY_MODE=live` the message went out and nothing is kept, so the
   *  route says so rather than answering empty. */
  "outbox-off": NoParams;
  /** No OpenID Connect client or no bot token on this deployment: the
   *  Telegram door is simply not offered. A configuration fact. */
  "telegram-off": NoParams;
  /** A Mini App launch that did not verify: a bad signature, a stale
   *  `auth_date`, or one already used. */
  "telegram-invalid": NoParams;
  /** Another account already carries this Telegram identity. */
  "telegram-taken": NoParams;
  /** Disconnecting Telegram from an account with no password would leave no
   *  way to sign in. */
  "last-credential": NoParams;
}

export type ApiErrorCode = keyof ApiErrorParams;

export const API_ERROR_CODES = [
  "fan-closed", "fan-choice",
  "pickup-already-rostered", "roster-full", "already-member", "application-pending", "application-missing", "player-claimed",
  "pickup-declined", "pickup-invite-limit", "pickup-link-invalid", "pickup-link-used",
  "support-closed",
  "invalid-payload",
  "not-found",
  "server-error",
  "forbidden",
  "unauthorized",
  "expected-websocket",
  "reauth-required",
  "invalid",
  "revoked",
  "expired",
  "rate",
  "would-replace",
  "team-referenced",
  "field-has-games",
  "field-last",
  "too-many-games",
  "unknown-team",
  "person-already-merged",
  "game-off-schedule",
  "erase-refused",
  "game-not-scheduled",
  "game-not-played",
  "opponent-undecided",
  "team-not-in-game",
  "spirit-off",
  "spirit-filed",
  "self-scoring-off",
  "push-off",
  "cards-off",
  "superadmin-exists",
  "email-registered",
  "superadmin-last",
  "superadmin-self",
  "superadmin-missing",
  "slug-taken",
  "slug-reserved",
  "slug-frozen",
  "tournament-missing",
  "tournament-archived",
  "already-adopted",
  "registration-closed",
  "roster-locked",
  "outbox-off",
  "telegram-off",
  "telegram-invalid",
  "telegram-taken",
  "last-credential",
] as const satisfies readonly ApiErrorCode[];

export function isApiErrorCode(v: unknown): v is ApiErrorCode {
  return typeof v === "string" && (API_ERROR_CODES as readonly string[]).includes(v);
}

/** JSON body of a refusal. `params` is absent when the sentence takes none. */
export interface ApiErrorBody {
  error: ApiErrorCode;
  params?: Record<string, string | number>;
  /** Machine token for whoever is reading a 400 off curl. Never rendered. */
  detail?: string;
}

/** No second argument when the code's sentence interpolates nothing. */
type ErrorArgs<C extends ApiErrorCode> = [keyof ApiErrorParams[C]] extends [never]
  ? []
  : [params: ApiErrorParams[C]];

/**
 * Build a refusal body. The overload is what makes forgetting a parameter a
 * compile error rather than a `{count}` shown to a volunteer.
 */
export function apiError<C extends ApiErrorCode>(code: C, ...rest: ErrorArgs<C>): ApiErrorBody {
  const params = rest[0] as Record<string, string | number> | undefined;
  return { error: code, ...(params !== undefined ? { params } : {}) };
}

/** Same, plus a machine token naming which parser refused. */
export function invalidPayload(detail: string): ApiErrorBody {
  return { error: "invalid-payload", detail };
}
